/**
 * PipelineGrid.jsx
 * =================
 * The Project Hub Phase 2 tile-grid view. Replaces the flat project list
 * with a stage-aware deal pipeline.
 *
 * Data source: GET /api/pipeline/projects?owner_id=...
 * Manual stage override: PATCH /api/pipeline/projects/{id}/stage
 *
 * Each tile shows:
 *  - Stage badge (Collecting bids / Bid sheet ready / Submitted / Funded / Walked)
 *  - Funded → green border + "Go to active build" link + green badge
 *  - Bid completeness bar (scopes_quoted / scopes_total)
 *  - Target budget vs lowest-bid total (color-coded if under budget)
 *  - Activity badges (awaiting bids, bids received)
 *  - Last activity timestamp (MM/DD/YYYY)
 *
 * Search + stage filter + "+ New deal" button at the top.
 */
import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import {
  Search, Plus, MapPin, Gavel, BadgeCheck, ArrowUpRight, Loader2,
  Briefcase, ChevronDown, Trash2, AlertTriangle, Rocket,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { formatDate } from '@/lib/dateUtils';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STAGES = {
  collecting:  { label: 'Collecting bids',   tone: 'bg-amber-50 text-amber-800 border-amber-200',   dot: '#f59e0b', accent: '#f59e0b' },
  sheet_ready: { label: 'Bid sheet ready',   tone: 'bg-purple-50 text-purple-800 border-purple-200',dot: '#8b5cf6', accent: '#8b5cf6' },
  submitted:   { label: 'Submitted to bank', tone: 'bg-blue-50 text-blue-800 border-blue-200',      dot: '#3b82f6', accent: '#3b82f6' },
  funded:      { label: 'Funded',            tone: 'bg-emerald-50 text-emerald-800 border-emerald-200', dot: '#10b981', accent: '#10b981' },
  walked:      { label: 'Walked / archived', tone: 'bg-slate-100 text-slate-600 border-slate-200',  dot: '#64748b', accent: '#64748b' },
};

export default function PipelineGrid({ user, onNewDeal, onOpenProject }) {
  const navigate = useNavigate();
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [stageFilter, setStageFilter] = useState('all');
  const [confirmDelete, setConfirmDelete] = useState(null); // project pending deletion
  const [deleting, setDeleting] = useState(false);
  const [confirmStart, setConfirmStart] = useState(null); // funded project pending "Start Project"
  const [starting, setStarting] = useState(false);

  useEffect(() => {
    if (!user?.id) return;
    setLoading(true);
    axios.get(`${API}/pipeline/projects`, { params: { owner_id: user.id } })
      .then(r => setProjects(Array.isArray(r.data) ? r.data : []))
      .catch(e => toast.error(e?.response?.data?.detail || 'Could not load pipeline'))
      .finally(() => setLoading(false));
  }, [user?.id]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return projects.filter(p => {
      if (stageFilter !== 'all' && p.stage !== stageFilter) return false;
      if (!q) return true;
      return (p.name || '').toLowerCase().includes(q) || (p.address || '').toLowerCase().includes(q);
    });
  }, [projects, search, stageFilter]);

  const stageCounts = useMemo(() => {
    const counts = { all: projects.length, collecting: 0, sheet_ready: 0, submitted: 0, funded: 0, walked: 0 };
    for (const p of projects) counts[p.stage] = (counts[p.stage] || 0) + 1;
    return counts;
  }, [projects]);

  const overrideStage = async (projectId, stage) => {
    const delays = [0, 600, 1500, 3000];
    let lastErr = null;
    for (let attempt = 0; attempt < delays.length; attempt++) {
      if (delays[attempt]) await new Promise(r => setTimeout(r, delays[attempt]));
      try {
        await axios.patch(`${API}/pipeline/projects/${projectId}/stage`, { stage });
        setProjects(prev => prev.map(p => {
          if (p.id !== projectId) return p;
          const newStage = stage || p.stage_auto;
          return { ...p, stage: newStage, stage_override: stage };
        }));
        toast.success(stage ? `Pinned to ${STAGES[stage]?.label}` : 'Reverted to auto-stage');
        return;
      } catch (e) {
        lastErr = e;
        if (e?.response?.status !== 429) break;
      }
    }
    toast.error(
      lastErr?.response?.status === 429
        ? 'Rate-limited — please try again in a moment.'
        : (lastErr?.response?.data?.detail || 'Could not update stage')
    );
  };

  const deleteDeal = async () => {
    if (!confirmDelete) return;
    setDeleting(true);
    try {
      await axios.delete(`${API}/projects/${confirmDelete.id}`);
      setProjects(prev => prev.filter(p => p.id !== confirmDelete.id));
      toast.success(`Deleted "${confirmDelete.name || 'deal'}"`);
      setConfirmDelete(null);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not delete this deal. Please try again.');
    } finally {
      setDeleting(false);
    }
  };

  const startDeal = async () => {
    if (!confirmStart) return;
    setStarting(true);
    try {
      await axios.post(`${API}/projects/${confirmStart.id}/start`, { user_id: user.id });
      const startedAt = new Date().toISOString();
      setProjects(prev => prev.map(p => (p.id === confirmStart.id ? { ...p, project_started_at: startedAt } : p)));
      toast.success(`"${confirmStart.name || 'Project'}" started — your awarded contractors have been notified.`);
      setConfirmStart(null);
      navigate(`/project/${confirmStart.id}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not start this project. Please try again.');
    } finally {
      setStarting(false);
    }
  };

  return (
    <div data-testid="pipeline-grid">
      {/* Header */}
      <div className="mb-5">
        <div className="flex items-center gap-2 flex-wrap mb-1">
          <span className="text-[11px] uppercase tracking-wide bg-slate-900 text-white px-2 py-0.5 rounded font-semibold">Deal pipeline</span>
          <span className="text-xs text-slate-500">— click any tile to drill in · funded deals get a green badge</span>
        </div>
      </div>

      {/* Toolbar */}
      <div className="flex flex-wrap items-center gap-3 mb-5">
        <div className="flex-1 max-w-md relative">
          <Search className="w-4 h-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2" />
          <input
            value={search}
            onChange={e => setSearch(e.target.value)}
            placeholder="Search deals by name or address…"
            className="w-full pl-10 pr-4 py-2 rounded-lg border border-slate-200 bg-white text-sm focus:outline-none focus:ring-2 focus:ring-blue-200"
            data-testid="pipeline-search"
          />
        </div>
        <select
          value={stageFilter}
          onChange={e => setStageFilter(e.target.value)}
          className="text-xs border border-slate-200 rounded-lg px-3 py-2 bg-white font-medium"
          data-testid="pipeline-stage-filter"
        >
          <option value="all">All stages · {stageCounts.all}</option>
          <option value="collecting">Collecting bids · {stageCounts.collecting}</option>
          <option value="sheet_ready">Bid sheet ready · {stageCounts.sheet_ready}</option>
          <option value="submitted">Submitted to bank · {stageCounts.submitted}</option>
          <option value="funded">Funded · {stageCounts.funded}</option>
          <option value="walked">Walked / archived · {stageCounts.walked}</option>
        </select>
        {onNewDeal && (
          <Button
            onClick={onNewDeal}
            className="bg-blue-600 hover:bg-blue-700 text-white"
            data-testid="pipeline-new-deal"
          >
            <Plus className="w-4 h-4 mr-1.5" /> New Bid Request
          </Button>
        )}
      </div>

      {/* Loading */}
      {loading && (
        <div className="py-20 text-center text-slate-500">
          <Loader2 className="w-6 h-6 animate-spin mx-auto" />
        </div>
      )}

      {/* Empty */}
      {!loading && filtered.length === 0 && (
        <div className="rounded-2xl border-2 border-dashed border-slate-300 p-16 text-center bg-white">
          <Briefcase className="w-10 h-10 mx-auto text-slate-400 mb-2" />
          <h3 className="text-base font-semibold text-slate-900">
            {search || stageFilter !== 'all' ? 'No deals match your filter' : 'No projects yet'}
          </h3>
          <p className="text-sm text-slate-600 mt-1 max-w-md mx-auto">
            {search || stageFilter !== 'all'
              ? 'Try clearing the search or switching stages.'
              : 'Start your first project to see it in the pipeline.'}
          </p>
        </div>
      )}

      {/* Grid */}
      {!loading && filtered.length > 0 && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="pipeline-tile-grid">
          {filtered.map(p => (
            <PipelineTile
              key={p.id}
              p={p}
              onOpen={() => {
                // Routing strategy:
                //   Funded / has locked schedule → /project/{id} (active build workspace)
                //   Anything else (collecting/sheet_ready/submitted/walked) →
                //     /project-hub?tab=bids&project={id} — the Bid Requests tab
                //     filtered to this project (which now shows a project header
                //     card + stats + scope list, so the PM can see bids in,
                //     bids awaiting, and send reminders without leaving the hub).
                if (p.stage === 'funded' || p.has_locked_schedule) {
                  navigate(`/project/${p.id}`);
                } else if (onOpenProject) {
                  // Deterministic in-hub open (fixes flaky same-route navigate).
                  onOpenProject(p.id);
                } else {
                  navigate(`/project-hub?tab=bids&project=${p.id}`);
                }
              }}
              onOverride={overrideStage}
              onDelete={() => setConfirmDelete(p)}
              onStart={() => setConfirmStart(p)}
            />
          ))}
          {onNewDeal && (
            <button
              onClick={onNewDeal}
              className="rounded-2xl border-2 border-dashed border-slate-300 hover:border-blue-400 hover:bg-blue-50 transition-colors flex flex-col items-center justify-center text-slate-500 hover:text-blue-700 p-10 min-h-[280px]"
              data-testid="pipeline-new-tile"
            >
              <Plus className="w-7 h-7 mb-2" />
              <div className="text-sm font-semibold">New Bid Request</div>
              <div className="text-xs">Start collecting bids on a property</div>
            </button>
          )}
        </div>
      )}

      {/* Delete confirmation */}
      {confirmDelete && (
        <div
          className="fixed inset-0 z-[100] flex items-center justify-center bg-slate-900/70 backdrop-blur-sm p-4"
          onClick={() => !deleting && setConfirmDelete(null)}
          data-testid="pipeline-delete-overlay"
        >
          <div
            className="bg-white rounded-2xl shadow-2xl w-full max-w-md p-6"
            onClick={e => e.stopPropagation()}
            data-testid="pipeline-delete-modal"
          >
            <div className="flex items-center gap-3 mb-3">
              <div className="w-10 h-10 rounded-full bg-red-50 flex items-center justify-center flex-shrink-0">
                <AlertTriangle className="w-5 h-5 text-red-600" />
              </div>
              <h3 className="text-base font-bold text-slate-900">Delete this deal?</h3>
            </div>
            <p className="text-sm text-slate-600">
              <span className="font-semibold text-slate-900">{confirmDelete.name || 'This deal'}</span> and all its
              bids, scopes and attachments will be permanently removed. This can&apos;t be undone.
            </p>
            <div className="mt-6 flex items-center justify-end gap-2">
              <Button
                variant="outline"
                onClick={() => setConfirmDelete(null)}
                disabled={deleting}
                data-testid="pipeline-delete-cancel"
              >
                Cancel
              </Button>
              <Button
                onClick={deleteDeal}
                disabled={deleting}
                className="bg-red-600 hover:bg-red-700 text-white"
                data-testid="pipeline-delete-confirm"
              >
                {deleting ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> : <Trash2 className="w-4 h-4 mr-1.5" />}
                {deleting ? 'Deleting…' : 'Delete deal'}
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Start Project confirmation */}
      {confirmStart && (
        <div
          className="fixed inset-0 z-[100] flex items-center justify-center bg-slate-900/70 backdrop-blur-sm p-4"
          onClick={() => !starting && setConfirmStart(null)}
          data-testid="pipeline-start-overlay"
        >
          <div
            className="bg-white rounded-2xl shadow-2xl w-full max-w-md p-6"
            onClick={e => e.stopPropagation()}
            data-testid="pipeline-start-modal"
          >
            <div className="flex items-center gap-3 mb-3">
              <div className="w-10 h-10 rounded-full bg-emerald-50 flex items-center justify-center flex-shrink-0">
                <Rocket className="w-5 h-5 text-emerald-600" />
              </div>
              <h3 className="text-base font-bold text-slate-900">Start this project?</h3>
            </div>
            <p className="text-sm text-slate-600">
              Starting <span className="font-semibold text-slate-900">{confirmStart.name || 'this project'}</span> moves
              it into your active builds, kicks off Phase 1, and emails a kickoff notice to every awarded contractor.
            </p>
            <div className="mt-6 flex items-center justify-end gap-2">
              <Button
                variant="outline"
                onClick={() => setConfirmStart(null)}
                disabled={starting}
                data-testid="pipeline-start-cancel"
              >
                Cancel
              </Button>
              <Button
                onClick={startDeal}
                disabled={starting}
                className="bg-emerald-600 hover:bg-emerald-700 text-white"
                data-testid="pipeline-start-confirm"
              >
                {starting ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> : <Rocket className="w-4 h-4 mr-1.5" />}
                {starting ? 'Starting…' : 'Start Project'}
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// =================================================================
// TILE
// =================================================================
function PipelineTile({ p, onOpen, onOverride, onDelete, onStart }) {
  const st = STAGES[p.stage] || STAGES.collecting;
  const isFunded = p.stage === 'funded';
  const isWalked = p.stage === 'walked';
  const bidPct = p.scopes_total > 0 ? Math.round((p.scopes_quoted / p.scopes_total) * 100) : 0;
  const savings = p.target_budget && p.low_total && p.low_total < p.target_budget
    ? p.target_budget - p.low_total : 0;
  const [menuOpen, setMenuOpen] = useState(false);

  const stopAndCall = (fn) => (e) => { e.stopPropagation(); fn?.(); };

  return (
    <div
      className={`text-left bg-white rounded-2xl border ${isFunded ? 'border-emerald-300' : isWalked ? 'border-slate-200 opacity-70' : 'border-slate-200 hover:border-blue-300'} hover:shadow-lg shadow-sm transition-all overflow-hidden group relative cursor-pointer`}
      onClick={onOpen}
      data-testid={`pipeline-tile-${p.id}`}
    >
      {/* Funded badge */}
      {isFunded && (
        <div className="absolute top-3 right-3 z-10 bg-emerald-600 text-white text-[10px] font-bold uppercase tracking-wide px-2 py-1 rounded-full inline-flex items-center gap-1">
          <BadgeCheck className="w-3 h-3" /> Funded
        </div>
      )}
      {/* Accent stripe */}
      <div style={{ height: 6, background: st.accent }} />

      <div className="p-4">
        {/* Title row */}
        <div className="flex items-start justify-between mb-1 gap-2">
          <div className="min-w-0 flex-1">
            <div className={`text-base font-semibold text-slate-900 truncate group-hover:text-blue-700 ${isFunded ? 'pr-16' : ''}`}>
              {p.name || 'Untitled project'}
            </div>
            <div className="text-xs text-slate-500 inline-flex items-center gap-1 mt-0.5">
              <MapPin className="w-3 h-3 shrink-0" /> <span className="truncate">{p.address || 'No address'}</span>
            </div>
          </div>
        </div>

        {/* Stage row + override */}
        <div className="mt-2 text-xs text-slate-600 flex items-center gap-2 flex-wrap">
          <button
            onClick={stopAndCall(() => setMenuOpen(o => !o))}
            className={`px-2 py-0.5 rounded inline-flex items-center gap-1 ${st.tone} border hover:brightness-95`}
            data-testid={`pipeline-stage-${p.id}`}
          >
            <span style={{ width: 6, height: 6, borderRadius: '50%', background: st.dot }} />
            {st.label}
            {p.stage_override && <span className="ml-1 text-[9px] uppercase font-bold tracking-wider">·pinned</span>}
            <ChevronDown className="w-3 h-3" />
          </button>
          <span className="text-slate-400">·</span>
          <span>{p.units > 1 ? `${p.units} units` : p.property_type || 'Project'}</span>
          {menuOpen && (
            <div
              onClick={stopAndCall(() => {})}
              className="absolute z-20 mt-12 ml-0 bg-white border border-slate-200 rounded-lg shadow-lg py-1 text-xs"
              data-testid={`pipeline-stage-menu-${p.id}`}
            >
              {['collecting','sheet_ready','submitted','funded','walked'].map(s => (
                <button
                  key={s}
                  onClick={stopAndCall(() => { onOverride(p.id, s); setMenuOpen(false); })}
                  className="block w-full text-left px-3 py-1.5 hover:bg-slate-50 whitespace-nowrap"
                >
                  Pin to <strong>{STAGES[s].label}</strong>
                </button>
              ))}
              {p.stage_override && (
                <button
                  onClick={stopAndCall(() => { onOverride(p.id, null); setMenuOpen(false); })}
                  className="block w-full text-left px-3 py-1.5 hover:bg-slate-50 text-blue-700 border-t border-slate-100 mt-1"
                >
                  Revert to auto ({STAGES[p.stage_auto]?.label})
                </button>
              )}
              {onDelete && (
                <button
                  onClick={stopAndCall(() => { onDelete(); setMenuOpen(false); })}
                  className="w-full text-left px-3 py-1.5 hover:bg-red-50 text-red-600 border-t border-slate-100 mt-1 inline-flex items-center gap-1.5 whitespace-nowrap"
                  data-testid={`pipeline-delete-${p.id}`}
                >
                  <Trash2 className="w-3 h-3" /> Delete deal
                </button>
              )}
            </div>
          )}
        </div>

        {/* Bid completeness */}
        {p.scopes_total > 0 && (
          <div className="mt-3">
            <div className="flex items-center justify-between text-[11px] text-slate-500 mb-1">
              <span>Bids in</span>
              <span>{p.scopes_quoted} of {p.scopes_total} scopes quoted</span>
            </div>
            <div className="h-1.5 bg-slate-100 rounded-full overflow-hidden">
              <div className="h-full rounded-full" style={{ width: `${bidPct}%`, background: st.accent }} />
            </div>
          </div>
        )}

        {/* Totals */}
        <div className="mt-3 text-[11px] grid grid-cols-2 gap-2">
          <div>
            <div className="text-slate-500">Target budget</div>
            <div className="font-semibold text-slate-900 tabular-nums">${(p.target_budget || 0).toLocaleString()}</div>
          </div>
          <div>
            <div className="text-slate-500">Lowest-bid total</div>
            <div className={`font-semibold tabular-nums ${savings ? 'text-emerald-700' : 'text-slate-900'}`}>
              ${(p.low_total || 0).toLocaleString()}
              {savings > 0 && <span className="ml-1 text-[10px]">↓${savings.toLocaleString()}</span>}
            </div>
          </div>
        </div>

        {/* Activity row */}
        <div className="mt-3 flex items-center gap-2 text-[11px] text-slate-600 flex-wrap">
          {p.awaiting_bids > 0 && (
            <span className="inline-flex items-center gap-1 bg-amber-50 text-amber-700 px-1.5 py-0.5 rounded">
              <Gavel className="w-3 h-3" /> {p.awaiting_bids} awaiting bids
            </span>
          )}
          {p.bids_in > 0 && (
            <span className="inline-flex items-center gap-1 bg-blue-50 text-blue-700 px-1.5 py-0.5 rounded">
              {p.bids_in} bids received
            </span>
          )}
          <span className="text-slate-400 ml-auto tabular-nums">
            {p.last_activity ? formatDate(p.last_activity) : '—'}
          </span>
        </div>

        {/* Funded footer */}
        {isFunded && (
          p.project_started_at ? (
            <div className="mt-3 pt-3 border-t border-emerald-100 flex items-center justify-between text-xs">
              <span className="text-emerald-700 font-semibold inline-flex items-center gap-1">
                <ArrowUpRight className="w-3.5 h-3.5" /> Active build
              </span>
              <span className="text-blue-700 underline group-hover:text-blue-900">Open project →</span>
            </div>
          ) : (
            <div className="mt-3 pt-3 border-t border-emerald-100">
              <button
                onClick={stopAndCall(() => onStart?.())}
                className="w-full inline-flex items-center justify-center gap-1.5 bg-emerald-600 hover:bg-emerald-700 text-white text-xs font-bold px-3 py-2 rounded-lg transition"
                data-testid={`pipeline-start-${p.id}`}
              >
                <Rocket className="w-3.5 h-3.5" /> Start Project
              </button>
              <p className="text-[10px] text-slate-400 text-center mt-1.5">Notifies awarded contractors & begins the build</p>
            </div>
          )
        )}
      </div>
    </div>
  );
}
